import { useState } from 'react'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material'
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material'
import Table, { Column } from '../../components/ui/Table'
import { useUsers } from '../../hooks/api/useUsers'
import { Booker } from '../../types'
import { format } from 'date-fns'
import UserForm from '../../components/forms/UserForm'
import ResetPasswordDialog from '../../components/forms/ResetPasswordDialog'

const UserListPage = () => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedUser, setSelectedUser] = useState<Booker | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')

  const { data: usersData, isLoading } = useUsers({
    page: page + 1,
    limit: rowsPerPage,
  })

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: Booker) => {
    setAnchorEl(event.currentTarget)
    setSelectedUser(user)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedUser(null)
  }

  const handleCreateUser = () => {
    setFormMode('create')
    setSelectedUser(null)
    setFormOpen(true)
  }

  const handleEditUser = () => {
    setFormMode('edit')
    setFormOpen(true)
    setAnchorEl(null)
  }

  const handleFormClose = () => {
    setFormOpen(false)
    setSelectedUser(null)
  }

  const handleResetPassword = () => {
    setResetOpen(true)
    setAnchorEl(null)
  }

  const handleResetClose = () => {
    setResetOpen(false)
    setSelectedUser(null)
  }

  const columns: Column<Booker>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 250,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      render: (value) => (
        <StatusChip
          label={value === 'active' ? 'Active' : 'Inactive'}
          variant={value === 'active' ? 'success' : 'neutral'}
        />
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      minWidth: 120,
      render: (value) => format(new Date(value), 'dd/MM/yyyy'),
    },
    {
      id: 'updatedAt',
      label: 'Last Updated',
      minWidth: 120,
      render: (value) => format(new Date(value), 'dd/MM/yyyy'),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 100,
      align: 'center',
      render: (_, row) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, row)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ]

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Users</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          Create User
        </Button>
      </Box>

      <Table
        columns={columns}
        data={usersData?.data?.map(user => ({ ...user, id: user._id })) || []}
        loading={isLoading}
        pagination={usersData ? {
          page,
          rowsPerPage,
          total: usersData.total,
          onPageChange: setPage,
          onRowsPerPageChange: setRowsPerPage,
        } : undefined}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditUser}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Name
        </MenuItem>
        <MenuItem onClick={handleResetPassword}>
          <LockResetIcon sx={{ mr: 1 }} />
          Reset Password
        </MenuItem>
      </Menu>

      <UserForm
        open={formOpen}
        onClose={handleFormClose}
        user={selectedUser}
        mode={formMode}
      />
      <ResetPasswordDialog
        open={resetOpen}
        onClose={handleResetClose}
        user={selectedUser}
      />
    </Box>
  )
}

export default UserListPage

